import {
  CompanyDealsTable,
  CompanyDescription,
  CompanyHeader,
  CompanyInvestors,
  CompanyMediaGallery,
  CompanyStat,
  CompanyStatMobile,
  VerticalNavigationScroll,
  CompanyHighlight,
  CompanyFundingRounds,
  Button,
  AppLayout,
  CompanyDocument,
} from '@components'
import { queryCompanyDetail, queryCompanyList } from '@queries'
import { ICompany, IDeal } from '@types'
import axios from 'axios'
import { getGraphQLData } from 'database/graphQL/apollo'
import { useAuth } from 'hooks'
import { AuthContext } from 'context/auth'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState, useContext } from 'react'
import {
  abbreviateNumber,
  percentageRoundTo,
  Mixpanel,
  formatCurrency,
  createHubspotDeal,
  standardDate,
} from '@utils'
import { NextSeo } from 'next-seo'

export const getStaticProps: GetStaticProps = async context => {
  const slug = context.params!.slug as string

  const [gqlResponse] = await Promise.all([
    getGraphQLData<{ company: ICompany }>(queryCompanyDetail({ slug })),
  ])

  let company = gqlResponse?.company

  if (!company) {
    return {
      notFound: true,
    }
  }

  return {
    props: { company: company },
    revalidate: 10,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const response = await getGraphQLData<{ allCompanies: ICompany[] }>(
    queryCompanyList,
  )
  const paths = response?.allCompanies.map(company => {
    return { params: { slug: company.slug } }
  })

  return {
    paths,
    fallback: true,
  }
}

const shareClassText = {
  common: 'Common',
  preferred: 'Preferred',
  convertible_note: 'C. Note',
}

const CompanyDetail: NextPage<{ company: ICompany }> = ({ company }) => {
  const router = useRouter()
  const { slug } = router.query
  const [deals, setDeals] = useState<IDeal[]>([])
  const authContext = useContext(AuthContext)

  const { currentUser } = useAuth()

  const [active, setActive] = useState('gallery')
  const [mainDeal, setMainDeal] = useState<IDeal | null>(null)

  useEffect(() => {
    if (!router.isReady) return
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', event => {
        if (event?.state?.path) router.push('/' + event.state.path)
      })
    }

    authContext.authenticated &&
      axios
        .get(process.env.NEXT_PUBLIC_API_URL + `/companies/mainDeal/${slug}`)
        .then(response => {
          response?.data && setMainDeal(response?.data.deals[0])
          response?.data && setDeals(response?.data.deals)
        })
        .catch(error => {
          console.log('error', error)
        })
  }, [currentUser, router.isReady, slug])

  useEffect(() => {
    Mixpanel.track('Visited-opportunity-detail', {
      opportunity: company?.name,
    })
  }, [])

  const SignupModal = () => {
    return (
      <div className="absolute left-0 right-0 top-80 z-50 mx-auto flex w-full flex-col items-center justify-center border border-primary bg-white p-8 md:w-[500px]">
        <h5 className="text-center text-xl font-semibold text-themeBlack">
          Please sign up for more information
        </h5>
        <div className="mt-8">
          <Link href="/signup">
            <Button>Sign Up</Button>
          </Link>
        </div>
      </div>
    )
  }

  const socialImageProps = company?.bgUrl
    ? [
        {
          url: company.bgUrl.url + '?w=800',
          width: 800,
          height: Math.round(
            (company.bgUrl.height / company.bgUrl.width) * 800,
          ),
          alt: `${company.name} background`,
          type: 'image/jpeg',
        },
      ]
    : []

  return (
    company && (
      <AppLayout>
        <NextSeo
          title={`Invest in ${company.name}`}
          description={company.hook}
          canonical={`https://www.marketxventures.com/invest/company/${company.slug}`}
          openGraph={{
            title: `Invest in ${company.name} | MarketX Ventures`,
            url: `https://www.marketxventures.com/invest/company/${company.slug}`,
            description: company.hook,
            siteName: 'MarketX Ventures',
            images: socialImageProps,
          }}
        />
        <div className="flex justify-between">
          <div className="xl:w-[20%]">
            <VerticalNavigationScroll
              company={company}
              active={active}
              authContext={authContext}
            />
          </div>

          <div className="mx-auto px-4 py-10 text-themeBlack sm:px-6 md:px-8 xl:relative xl:left-[4.6rem] xl:mx-0 xl:w-1/2 xl:py-6 2xl:w-2/3">
            <CompanyHeader mainDeal={mainDeal} company={company} />

            {/* Mobile Stats */}
            <div className="flex w-full pt-4 md:pt-12">
              {currentUser && mainDeal?.showFullDealDetail && (
                <>
                  {mainDeal?.sharePrice && (
                    <CompanyStatMobile
                      title={`${abbreviateNumber(mainDeal?.sharePrice)}` || ''}
                      subtitle="Share Price"
                    />
                  )}
                  {mainDeal?.impliedValuation && (
                    <CompanyStatMobile
                      title={
                        `${abbreviateNumber(mainDeal?.impliedValuation)}` || ''
                      }
                      subtitle="Implied Valuation"
                    />
                  )}
                  {mainDeal?.valuation && (
                    <CompanyStatMobile
                      title={`${abbreviateNumber(mainDeal?.valuation)}` || ''}
                      subtitle="Last Round Valuation"
                    />
                  )}

                  <CompanyStatMobile
                    title={`${percentageRoundTo(
                      mainDeal?.managementFee,
                    )} + ${percentageRoundTo(mainDeal?.carryFee)}`}
                    subtitle="Fee Structure"
                  />

                  {mainDeal?.shareClass && (
                    <CompanyStatMobile
                      title={shareClassText[mainDeal?.shareClass]}
                      subtitle="Share Class"
                    />
                  )}
                </>
              )}
            </div>

            <CompanyMediaGallery
              gallery={company.mediaGallery}
              setActive={setActive}
            />

            <CompanyInvestors
              investors={company.investors}
              setActive={setActive}
            />
            <div className={`relative ${!currentUser ? 'h-[800px]' : ''}`}>
              {!currentUser && (
                <>
                  {' '}
                  <div className="absolute inset-0 z-40  bg-gradient-to-b from-white" />{' '}
                  <SignupModal />{' '}
                </>
              )}

              <CompanyHighlight
                highlightBlocks={company.highlightBlocks || []}
                highlightText={company.highlightText || []}
              />

              {currentUser && deals?.length > 1 ? (
                <CompanyDealsTable company={company} deals={deals} />
              ) : (
                <></>
              )}
              {currentUser && (
                <>
                  <CompanyDescription
                    description={company.modularDescription}
                    setActive={setActive}
                    currentUser={currentUser}
                    mainDealsId={mainDeal?.id}
                    title={company.name}
                    companySlug={company.slug}
                    // setTableOfContent={setTableOfContent} // To Be Used Later
                  />
                  {company && company?.fundingRounds?.length ? (
                    <CompanyFundingRounds
                      setActive={setActive}
                      fundingRounds={company.fundingRounds}
                    />
                  ) : (
                    <div />
                  )}

                  <div className="mb-4 mt-12 w-full border-t border-gray-200 pt-8">
                    <Link href="/faq">
                      <Button>Frequently Asked Questions</Button>
                    </Link>
                  </div>

                  {authContext?.authenticated && mainDeal && (
                    <Link
                      href={`/companies/${company.slug}/deals/${mainDeal.id}/subscribe`}>
                      <Button
                        className="w-full xl:hidden"
                        showLoadingSpinnerOnClick={true}>
                        Invest in Deal
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Static sidebar for desktop */}
          <div className="-mt-10 ml-32 hidden w-[31%] flex-col border-l border-primary/10 bg-themeIvory xl:flex">
            <div className="sticky top-32 z-[30] mb-20 w-full p-4">
              {!currentUser && (
                <Link href={`/signup?loginRedirect=/companies/${slug}`}>
                  <Button size="xl" className="mb-3 w-full">
                    Request access
                  </Button>
                </Link>
              )}

              {currentUser && mainDeal && (
                <>
                  {mainDeal.closingAt && (
                    <div className="m-3">
                      <p className="text-sm font-bold text-gray-900">
                        Deal closing: {standardDate(mainDeal.closingAt)}
                      </p>
                    </div>
                  )}
                  <Link
                    href={`/companies/${company.slug}/deals/${mainDeal.id}/subscribe`}>
                    <Button
                      size="xl"
                      className="mb-3 w-full"
                      showLoadingSpinnerOnClick={true}>
                      Invest in Deal
                    </Button>
                  </Link>
                </>
              )}

              <Link
                href={process.env.NEXT_PUBLIC_DEFAULT_CALENDLY_URL!}
                target="_blank"
                onClick={() =>
                  authContext?.user &&
                  createHubspotDeal(authContext.user, company)
                }>
                <Button dark size="xl" className="mb-3 w-full">
                  Discuss with Advisor
                </Button>
              </Link>

              {mainDeal?.minimumInvestment && (
                <CompanyStat
                  subtitle={`${formatCurrency(mainDeal?.minimumInvestment)}`}
                  tooltip="This is the minimum investment amount per investor for a specific deal, set by MarketX Ventures."
                />
              )}
              {mainDeal && currentUser && <CompanyDocument />}
            </div>

            {/* {company?.team && company?.team?.length > 0 && (
                <div className="px-4 pb-16">
                  <CompanyTeam team={company.team} list grayAvatar />
                </div>
              )} */}
          </div>
        </div>
      </AppLayout>
    )
  )
}

export default CompanyDetail
